import { useState } from 'react';
import type { PersonRef } from '../../domain/people';
import { fullName } from '../../utils/people';
import UserAvatar from '../UserAvatar';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { X } from 'lucide-react';

/**
 * Who a new message goes to: the people I can start a conversation with
 * (018 FR-002), narrowed by a filter, one or more chosen. The chosen show as
 * chips above the list.
 */
export default function RecipientPicker({
  people,
  selected,
  onChange,
}: {
  people: PersonRef[];
  selected: string[];
  onChange: (ids: string[]) => void;
}) {
  const [filter, setFilter] = useState('');
  const query = filter.trim().toLowerCase();
  const shown = people.filter((p) => fullName(p).toLowerCase().includes(query));
  const chosen = people.filter((p) => selected.includes(p.id));

  const toggle = (id: string, on: boolean) =>
    onChange(on ? [...selected, id] : selected.filter((x) => x !== id));

  return (
    <div className="space-y-3">
      {chosen.length > 0 && (
        <ul aria-label="To" className="flex flex-wrap gap-2">
          {chosen.map((p) => (
            <li
              key={p.id}
              className="flex items-center gap-1 rounded-full bg-secondary py-1 pl-3 pr-1 text-sm font-medium text-secondary-foreground"
            >
              {fullName(p)}
              <button
                type="button"
                aria-label={`Remove ${fullName(p)}`}
                className="rounded-full p-1 hover:bg-background"
                onClick={() => toggle(p.id, false)}
              >
                <X className="size-3" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <Input
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        placeholder="Find someone"
        aria-label="Find someone"
        className="rounded-2xl"
      />
      <ul aria-label="People you can message" className="space-y-2">
        {shown.map((p) => {
          const id = `recipient-${p.id}`;
          return (
            <li key={p.id} className="flex items-center gap-3 rounded-3xl bg-card px-4 py-3">
              <Checkbox
                id={id}
                checked={selected.includes(p.id)}
                onCheckedChange={(on) => toggle(p.id, on === true)}
              />
              <UserAvatar person={p} className="size-9" />
              <Label htmlFor={id} className="flex-1 cursor-pointer font-semibold">
                {fullName(p)}
              </Label>
            </li>
          );
        })}
        {shown.length === 0 && (
          <li className="px-4 py-3 text-sm text-muted-foreground">Nobody by that name.</li>
        )}
      </ul>
    </div>
  );
}
